import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AppShell from '@/components/AppShell';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    job_title: '',
    company: '',
    department: '',
    industry: '',
    level: '',
    target_role: '',
    country: '',
    city: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  const departments = ['Finance', 'Marketing', 'Engineering', 'HR', 'Sales', 'Operations', 'Product', 'Other'];
  const industries = ['Technology', 'Finance', 'Healthcare', 'Pharmaceutical', 'Retail', 'Consulting', 'Other'];
  const levels = ['Entry', 'Mid-Level', 'Senior', 'Manager', 'Director', 'VP+'];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/users/profile`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      setUser(response.data);
      setFormData({
        name: response.data.name || '',
        job_title: response.data.job_title || '',
        company: response.data.company || '',
        department: response.data.department || '',
        industry: response.data.industry || '',
        level: response.data.level || '',
        target_role: response.data.target_role || '',
        country: response.data.country || '',
        city: response.data.city || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.put(
        `${API}/users/profile`,
        formData,
        {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }
      );
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-navy-900 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading profile...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Profile Settings
            </h1>
            <p className="text-gray-600">Manage your account information</p>
          </div>
        </div>

        <div className="bg-white p-6 md:p-8 rounded-lg border border-gray-200">
          <div className="space-y-6">
            {/* Personal Info */}
            <div>
              <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                Personal Information
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Email</label>
                  <Input
                    value={user.email}
                    disabled
                    className="bg-gray-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Name</label>
                  <Input
                    data-testid="name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                  />
                </div>
              </div>
            </div>

            {/* Work Info */}
            <div className="pt-6 border-t border-gray-200">
              <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                Work Information
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Job Title</label>
                  <Input
                    data-testid="job-title-input"
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    placeholder="Senior Financial Analyst"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Company</label>
                  <Input
                    data-testid="company-input"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Acme Corp"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Department</label>
                  <Select value={formData.department} onValueChange={(value) => setFormData({ ...formData, department: value })}>
                    <SelectTrigger data-testid="department-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((dept) => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Industry</label>
                  <Select value={formData.industry} onValueChange={(value) => setFormData({ ...formData, industry: value })}>
                    <SelectTrigger data-testid="industry-select">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Level</label>
                  <Select value={formData.level} onValueChange={(value) => setFormData({ ...formData, level: value })}>
                    <SelectTrigger data-testid="level-select">
                      <SelectValue placeholder="Select your level" />
                    </SelectTrigger>
                    <SelectContent>
                      {levels.map((lvl) => (
                        <SelectItem key={lvl} value={lvl}>{lvl}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Target Role</label>
                  <Input
                    data-testid="target-role-input"
                    value={formData.target_role}
                    onChange={(e) => setFormData({ ...formData, target_role: e.target.value })}
                    placeholder="Lead Financial Analyst"
                  />
                </div>
              </div>
            </div>

            {/* Location Info */}
            <div className="pt-6 border-t border-gray-200">
              <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                Location Information
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Help us understand our global user base. Your location will appear in platform analytics.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Country</label>
                  <Input
                    data-testid="country-input"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="e.g., Nigeria, United Kingdom, Canada"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>City</label>
                  <Input
                    data-testid="city-input"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., Lagos, London, Toronto"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-6 border-t border-gray-200">
              <Button
                data-testid="save-profile-btn"
                onClick={handleSave}
                disabled={isSaving}
                className="w-full py-3 rounded-lg font-medium transition-colors duration-200"
                style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default Profile;